import React from 'react';

import _ from 'lodash';
import { GitHub, SearchOutlined } from '@mui/icons-material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  Alert,
  AppBar,
  Box,
  CircularProgress,
  Container,
  createTheme,
  Divider,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

import { Log } from '@/parsing/Log';
import { CombinedLogParserWorkerClient } from '@/parsing/parsers/CombinedLogParserWorkerClient';
import { GzCustomDecompressor } from '@/parsing/parsers/ios/gz-custom-module/GzCustomDecompressor';
import { SourceLogsBundle } from '@/parsing/SourceLogsBundle';
import { deserializeLog } from '@/workers/serialization/SerializedLog';

import { Analytics } from './analytics/Analytics';
import { AnalyticsEvent } from './analytics/AnalyticsEvent';
import { AppSettingsManager, AppSettingsMenuItem } from './AppSettings';
import { FileReceiverButton, FileReceiverMenuItem, ReceivedFile } from './components/FileReceiver';
import { OpenLogAsText } from './components/OpenLogAsText';
import { RecentFilesList, RecentFilesManager } from './components/RecentFiles';
import {
  SearchSuggestion,
  SearchTextField,
  SearchTextFieldDelegate,
  SearchTextFieldIndex,
} from './components/SearchTextField';
import { UseChromeInsteadOfSafari } from './components/UseChromeInsteadOfSafari';
import { TextFilterHighlighter } from './log-viewer/highlighting/text/TextFilterHighlighter';
import { TextSearchHighlighter } from './log-viewer/highlighting/text/TextSearchHighlighter';
import { LogModuleChecklistMenuItem } from './log-viewer/LogModuleChecklistMenuItem';
import { LogViewer } from './log-viewer/LogViewer';
import { LogViewerPostProcessing } from './log-viewer/LogViewerPostProcessing';
import {
  makeEmptyNetworkOutcomeFilter,
  NetworkOutcomeFilter,
} from './log-viewer/NetworkOutcomeFilter';
import { NetworkOutcomeFilterMenuItem } from './log-viewer/NetworkOutcomeFilterMenuItem';
import { FoundOccurancesPerHighlighter } from './log-viewer/post-processing/PostProcessor';
import { PostProcessingDelegate } from './log-viewer/table/LogViewerTable';

import './styles.scss';

declare let __APP_VERSION__: string | undefined;

interface State {
  logContext?: LogContext;
  logParsingProgress?: string;
  shouldShowNoAnalyticsBadge: boolean;
  networkOutcomeFilter: NetworkOutcomeFilter;
  showNoBugButtonToast: boolean;
  skippedFilesNotice?: string;
}

interface LogContext {
  log: Log;
  postProcessing: LogViewerPostProcessing;

  searchTextFieldIndex?: SearchTextFieldIndex;
  filterTextFieldIndex?: SearchTextFieldIndex;
}

export class App
  extends React.Component<Record<never, any>, State>
  implements SearchTextFieldDelegate, PostProcessingDelegate
{
  private settingsManager = new AppSettingsManager();
  private recentFilesManager = new RecentFilesManager();
  private logViewerRef = React.createRef<LogViewer>();
  private postProcessingProgressBarRef = React.createRef<HTMLDivElement>();
  private searchInputHtmlId = 'searchInput';
  private pendingSearchResolve?: () => void;

  private theme = createTheme({
    typography: {
      allVariants: {
        fontFamily: 'var(--font-family)',
      },
    },
  });

  constructor(props: Record<never, any>) {
    super(props);

    this.settingsManager.load();
    this.state = {
      shouldShowNoAnalyticsBadge: this.settingsManager.settings.disableAnalytics || false,
      networkOutcomeFilter: makeEmptyNetworkOutcomeFilter(),
      showNoBugButtonToast: false,
    };
  }

  private moduleChecklistMenuItem() {
    if (!this.state.logContext) {
      return null;
    }

    return (
      <LogModuleChecklistMenuItem
        shouldShowModule={this.state.logContext.postProcessing.shouldShowModule}
        onChange={(newModules) => {
          const pp = this.state.logContext?.postProcessing;
          if (pp) {
            pp.shouldShowModule = newModules;
            this.updateLogContext({ postProcessing: pp });
          }
        }}
      />
    );
  }

  private networkOutcomeFilterMenuItem() {
    if (!this.state.logContext) {
      return null;
    }
    return (
      <NetworkOutcomeFilterMenuItem
        filter={this.state.networkOutcomeFilter}
        onChange={(f) => this.setState({ networkOutcomeFilter: f })}
      />
    );
  }

  componentDidMount() {
    // Initialize recent files manager (async for IndexedDB)
    this.recentFilesManager.init().then(() => {
      this.forceUpdate();
    });

    const anyWindow: any = window;
    anyWindow.enableAnalytics = () => {
      this.settingsManager.settings.disableAnalytics = false;
      this.settingsManager.save();
    };
    anyWindow.disableAnalytics = () => {
      this.settingsManager.settings.disableAnalytics = true;
      this.settingsManager.save();
    };
    Analytics.setDisabled(this.settingsManager.settings.disableAnalytics || false);

    // Show confirmation dialog when user tries to reload/close page with an open file
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    this.loadDevelopmentFileIfAny();

    this.settingsManager.onChange = () => {
      console.log('Settings: onChange', this.settingsManager.settings);
      this.setState({
        shouldShowNoAnalyticsBadge: this.settingsManager.settings.disableAnalytics || false,
      });
      this.forceUpdate();
    };

    this.setPostProcessingProgressBarVisibility(false);

    window.addEventListener('keydown', (event) => {
      if (event.metaKey && event.key === 'f') {
        const element = document.getElementById(this.searchInputHtmlId) as HTMLInputElement;
        if (!element) {
          return;
        }

        const isAlreadyInFocus = element === document.activeElement;
        if (isAlreadyInFocus) {
          if (this.settingsManager.settings.disableBuiltInSearch === true) {
            element.select();
            event.preventDefault();
          }
        } else {
          element.focus();
          element.select();
          event.preventDefault();
        }
      }
    });

    Analytics.log(AnalyticsEvent.MainPageOpened);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  private handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (this.state.logContext) {
      // Modern browsers ignore the custom message and show their own generic message
      event.preventDefault();
    }
  };

  render() {
    return (
      <div>
        <ThemeProvider theme={this.theme}>
          <AppBar position="fixed" color="primary" className="app-bar">
            <Toolbar>
              <Stack direction="column">
                <Tooltip title={`Version: ${__APP_VERSION__}`}>
                  <a href="./" style={{ textDecoration: 'none' }}>
                    <img className="bolt-logo" src="./bolt_white.svg" />
                    <Typography variant="caption" noWrap component="div" style={{ color: 'white' }}>
                      Log Viewer
                    </Typography>
                  </a>
                </Tooltip>
              </Stack>

              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ width: '16px', display: { xs: '', sm: 'block' } }}
              />

              <FileReceiverMenuItem
                handleFiles={(receivedFiles) => {
                  this.parseLogFromReceivedFileList(receivedFiles);
                }}
                showTitle={typeof this.state.logContext === 'undefined'}
              />

              {this.state.logContext ? (
                <>
                  <Divider sx={{ height: 28, m: 0.5, marginRight: '8px' }} orientation="vertical" />

                  <Container style={{ padding: 0 }}>
                    <Stack direction="row">
                      <SearchTextField
                        searchId={TextSearchHighlighter.id}
                        inputHtmlId={this.searchInputHtmlId}
                        icon={<SearchOutlined />}
                        placeholder="Search by text…"
                        delegate={this}
                        term={this.state.logContext.postProcessing.searchString}
                        index={this.state.logContext.searchTextFieldIndex}
                        caseSensitive={this.state.logContext.postProcessing.searchCaseSensitive}
                        regex={this.state.logContext.postProcessing.searchUseRegex}
                        suggestions={this.getSearchSuggestions()}
                      />

                      <SearchTextField
                        searchId={TextFilterHighlighter.id}
                        icon={<FilterAltOutlinedIcon />}
                        placeholder="Filter by text…"
                        delegate={this}
                        term={this.state.logContext.postProcessing.filterString}
                        index={this.state.logContext.filterTextFieldIndex}
                        caseSensitive={this.state.logContext.postProcessing.filterCaseSensitive}
                        regex={this.state.logContext.postProcessing.filterUseRegex}
                      />
                    </Stack>
                  </Container>

                  {this.moduleChecklistMenuItem()}

                  {this.networkOutcomeFilterMenuItem()}

                  <Divider
                    sx={{ height: 28, m: 0.5, marginRight: '24px' }}
                    orientation="vertical"
                  />
                </>
              ) : null}

              {/* <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}
                        ></Typography> */}

              <Stack direction="row" spacing="4px">
                {this.state.logContext ? <OpenLogAsText log={this.state.logContext.log} /> : null}

                <AppSettingsMenuItem
                  settingsManager={this.settingsManager}
                  onNoBugButtonFound={() => this.setState({ showNoBugButtonToast: true })}
                />
              </Stack>
            </Toolbar>
          </AppBar>
          <Toolbar />

          <div className="logViewerContainer">
            {typeof this.state.logParsingProgress !== 'undefined' ? (
              <div className="mainPageCenter">
                <Stack direction="column" alignItems="center" alignContent="center">
                  <CircularProgress />

                  <div className="parsingProgressLabel">{this.state.logParsingProgress}%</div>
                </Stack>
              </div>
            ) : this.state.logContext ? (
              <LogViewer
                ref={this.logViewerRef}
                log={this.state.logContext.log}
                postProcessing={this.state.logContext.postProcessing}
                postProcessingDelegate={this}
                settings={_.cloneDeep(this.settingsManager.settings)}
                networkOutcomeFilter={this.state.networkOutcomeFilter}
                onSetShowBookmarksInSettings={(showBookmarks) => {
                  this.settingsManager.settings.showBookmarks = showBookmarks;
                  this.settingsManager.save();
                }}
                onNoBugButtonFound={() => this.setState({ showNoBugButtonToast: true })}
              />
            ) : (
              <div>
                <div className="mainPageCenter">
                  <h3>Log viewer for Bolt native mobile applications</h3>

                  <span>It accepts the following logs:</span>

                  <ul>
                    <li>
                      compressed Rider iOS log (<span className="monospace">log.txt.gz</span>)
                    </li>
                    <li>
                      uncompressed Rider iOS log (<span className="monospace">log.txt</span>)
                    </li>

                    <li>
                      compressed Rider Android log (<span className="monospace">logs.zip</span>)
                    </li>

                    <li>
                      uncompressed Rider Android log (<span className="monospace">logs.log</span>)
                    </li>

                    <li>
                      Eater (iOS & Android) log (<span className="monospace">log-X.log</span>)
                    </li>
                  </ul>

                  <FileReceiverButton
                    handleFiles={(receivedFiles) => {
                      this.parseLogFromReceivedFileList(receivedFiles);
                    }}
                  />

                  <div style={{ marginTop: 64 }} />
                  <Stack direction="column" alignItems="center" spacing="1em">
                    <div>Or you use this button to decompress gzipped logs:</div>

                    <FileReceiverButton
                      title="DECOMPRESS GZIPPED LOGS"
                      handleFiles={(receivedFiles) => {
                        this.decompressLogFromReceivedFileList(receivedFiles);
                      }}
                    />
                  </Stack>

                  <div style={{ marginTop: 48 }} />
                  <RecentFilesList
                    recentFilesManager={this.recentFilesManager}
                    onFileSelected={(files) => this.parseLogFromReceivedFileList(files)}
                  />
                </div>
              </div>
            )}
          </div>

          <UseChromeInsteadOfSafari />

          <Box sx={{ width: '100%' }} ref={this.postProcessingProgressBarRef}>
            <LinearProgress />
          </Box>

          {this.state.logContext ? null : (
            <a
              className="githubLink"
              target="_blank"
              href="https://github.com/bolteu/mobile-app-logviewer/"
              rel="noreferrer"
            >
              <IconButton>
                <GitHub />
              </IconButton>
            </a>
          )}

          {this.state.shouldShowNoAnalyticsBadge ? (
            <span className="analytics-disabled">Analytics disabled</span>
          ) : null}

          <Snackbar
            open={this.state.showNoBugButtonToast}
            autoHideDuration={5000}
            onClose={() => this.setState({ showNoBugButtonToast: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              severity="warning"
              onClose={() => this.setState({ showNoBugButtonToast: false })}
            >
              No &quot;Bug Report Button Tap&quot; event found in this log.
            </Alert>
          </Snackbar>

          <Snackbar
            open={typeof this.state.skippedFilesNotice !== 'undefined'}
            autoHideDuration={10000}
            onClose={() => this.setState({ skippedFilesNotice: undefined })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="info" onClose={() => this.setState({ skippedFilesNotice: undefined })}>
              {this.state.skippedFilesNotice}
            </Alert>
          </Snackbar>
        </ThemeProvider>
      </div>
    );
  }

  private updateLogContext(u: Partial<LogContext>) {
    const logContext = this.state.logContext;
    if (!logContext) {
      console.error('LogContext is expected to be existing');
      return;
    }

    const newLogContext: LogContext = {
      ...logContext,
      ...u,
    };
    this.setState({ logContext: newLogContext });
  }

  private loadDevelopmentFileIfAny() {
    const fileName = new URLSearchParams(window.location.search).get('devfile');
    if (!fileName) {
      return;
    }

    const client = new XMLHttpRequest();
    client.open('GET', fileName);
    client.responseType = 'arraybuffer';
    client.onreadystatechange = () => {
      if (!client.response) {
        return;
      }

      const file: ReceivedFile = {
        fileName,
        contents: client.response,
      };
      this.parseLogFromReceivedFileList([file]);
    };
    client.send();
  }

  private async parseLogFromReceivedFileList(receivedFiles: ReceivedFile[]) {
    const bundle: SourceLogsBundle = {
      sourceLogs: receivedFiles.map((f) => {
        return {
          filePath: f.fileName,
          contents: f.contents,
        };
      }),
    };

    const client = new CombinedLogParserWorkerClient();

    try {
      this.setState({ logParsingProgress: '0' });

      const serializedLog = await client.parse(bundle, (progress) => {
        const progressPercent = (progress * 100).toFixed(0);
        this.setState({ logParsingProgress: progressPercent });
      });

      client.shutdown();

      const log = deserializeLog(serializedLog);
      console.log('Parsed log', log);

      const shouldShowModule = new Map<string, boolean>();
      shouldShowModule.set('', true); // Entries without module.
      log.modules.forEach((m) => shouldShowModule.set(m, true));

      // If some uploaded files had a different log format and were skipped,
      // build a friendly notice explaining which ones and why.
      let skippedFilesNotice: string | undefined;
      if (log.skippedFiles && log.skippedFiles.length > 0) {
        const names = log.skippedFiles.map((f) => f.split('/').pop() ?? f).join(', ');
        skippedFilesNotice =
          `${log.skippedFiles.length} file(s) were a different log format than the first ` +
          `and can't be combined, so only the first format is shown. Skipped: ${names}`;
      }

      this.setState({
        logParsingProgress: undefined,
        skippedFilesNotice,
        logContext: {
          postProcessing: {
            shouldShowModule,

            filterString: '',
            filterCaseSensitive: this.settingsManager.settings.filterCaseSensitive ?? false,
            filterUseRegex: this.settingsManager.settings.filterRegex ?? false,

            searchString: '',
            searchCaseSensitive: this.settingsManager.settings.searchCaseSensitive ?? false,
            searchUseRegex: this.settingsManager.settings.searchRegex ?? false,
          },
          log,
        },
      });

      Analytics.log(AnalyticsEvent.LogParsed, {
        timelineEnabled: this.settingsManager.settings.enableTimelineV2,
        metadataParser: log.parserMetadata.parser,
        metadataApp: log.parserMetadata.app,
      });

      // Add to recent files (with file handles for File System Access API)
      for (const f of receivedFiles) {
        await this.recentFilesManager.addFile(f.fileName, f.fileName, f.handle || null, {
          parser: log.parserMetadata.parser,
          app: log.parserMetadata.app,
        });
      }
    } catch (error) {
      client.shutdown();
      this.setState({ logParsingProgress: undefined });

      console.error(error);
      alert(error);
    }
  }

  private async decompressLogFromReceivedFileList(receivedFiles: ReceivedFile[]) {
    if (receivedFiles.length === 0) {
      return;
    }

    const file = receivedFiles[0];
    const originalBytesArray = new Uint8Array(file.contents);
    const logText = await GzCustomDecompressor.decompress(originalBytesArray);
    Analytics.log(AnalyticsEvent.LogDecompressed);
    OpenLogAsText.downloadRawText(logText, OpenLogAsText.composeLogFileName(file.fileName));
  }

  private getSearchSuggestions(): SearchSuggestion[] {
    const allSuggestions: SearchSuggestion[] = [
      { label: 'Go to Bug Report', searchTerm: 'Bug Report Button Tap' },
      { label: 'Go to Bug Report', searchTerm: 'Bug Button pressed' },
    ];

    // Only show suggestions if the search term exists in the log
    const log = this.state.logContext?.log;
    if (!log) {
      return [];
    }

    return allSuggestions.filter((suggestion) =>
      log.entries.some((entry) => entry.content?.includes(suggestion.searchTerm)),
    );
  }

  // MARK: - SearchTextFieldDelegate

  search(searchId: string, term: string): Promise<void> {
    if (typeof this.state.logContext === 'undefined') {
      return Promise.resolve();
    }

    const pp = this.state.logContext.postProcessing;

    switch (searchId) {
      case TextFilterHighlighter.id:
        pp.filterString = term;
        Analytics.log(AnalyticsEvent.FilterTermChanged, { regex: pp.filterUseRegex });
        this.updateLogContext({ postProcessing: pp });
        break;
      case TextSearchHighlighter.id:
        pp.searchString = term;
        Analytics.log(AnalyticsEvent.SearchTermChanged, { regex: pp.filterUseRegex });
        this.updateLogContext({ postProcessing: pp });
        break;
      default:
        return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.pendingSearchResolve = resolve;
    });
  }

  jumpToSearchTerm(searchId: string, termIndex: number): void {
    if (typeof this.state.logContext === 'undefined') {
      return;
    }

    const logViewer = this.logViewerRef.current;
    if (logViewer === null) {
      return;
    }

    switch (searchId) {
      case TextFilterHighlighter.id:
        const filterIndex = this.state.logContext.filterTextFieldIndex;
        if (filterIndex) {
          this.updateLogContext({
            filterTextFieldIndex: {
              currentIndex: termIndex,
              totalAmount: filterIndex.totalAmount,
            },
          });
        }
        break;
      case TextSearchHighlighter.id:
        const searchIndex = this.state.logContext.searchTextFieldIndex;
        if (searchIndex) {
          this.updateLogContext({
            searchTextFieldIndex: {
              currentIndex: termIndex,
              totalAmount: searchIndex.totalAmount,
            },
          });
        }
        break;
    }

    logViewer.jumpToSearchTerm(searchId, termIndex);
  }

  clearSearch(searchId: string): void {
    if (typeof this.state.logContext === 'undefined') {
      return;
    }

    const pp = this.state.logContext.postProcessing;

    switch (searchId) {
      case TextFilterHighlighter.id:
        pp.filterString = '';
        this.updateLogContext({ postProcessing: pp });
        break;
      case TextSearchHighlighter.id:
        pp.searchString = '';
        this.updateLogContext({ postProcessing: pp });
        break;
    }
  }

  toggleRegex(searchId: string): void {
    if (typeof this.state.logContext === 'undefined') {
      return;
    }

    const pp = this.state.logContext.postProcessing;

    switch (searchId) {
      case TextFilterHighlighter.id:
        pp.filterUseRegex = !pp.filterUseRegex;

        this.settingsManager.settings.filterRegex = pp.filterUseRegex;
        this.settingsManager.save();

        this.updateLogContext({ postProcessing: pp });
        break;
      case TextSearchHighlighter.id:
        pp.searchUseRegex = !pp.searchUseRegex;

        this.settingsManager.settings.searchRegex = pp.searchUseRegex;
        this.settingsManager.save();

        this.updateLogContext({ postProcessing: pp });
        break;
    }
  }

  toggleCaseSensitivity(searchId: string): void {
    if (typeof this.state.logContext === 'undefined') {
      return;
    }

    const pp = this.state.logContext.postProcessing;

    switch (searchId) {
      case TextFilterHighlighter.id:
        pp.filterCaseSensitive = !pp.filterCaseSensitive;

        this.settingsManager.settings.filterCaseSensitive = pp.filterCaseSensitive;
        this.settingsManager.save();

        this.updateLogContext({ postProcessing: pp });
        break;
      case TextSearchHighlighter.id:
        pp.searchCaseSensitive = !pp.searchCaseSensitive;

        this.settingsManager.settings.searchCaseSensitive = pp.searchCaseSensitive;
        this.settingsManager.save();

        this.updateLogContext({ postProcessing: pp });
        break;
    }
  }

  // MARK: - PostProcessingDelegate

  private setPostProcessingProgressBarVisibility(v: boolean) {
    const progressBar = this.postProcessingProgressBarRef.current;
    if (progressBar) {
      progressBar.style.display = v ? 'block' : 'none';
    }
  }

  postProcessingOperationStarted(): void {
    console.log('PP started');
    this.setPostProcessingProgressBarVisibility(true);
  }

  postProcessingOperationFailed(error: any): void {
    console.log('PP failed', error);
    this.setPostProcessingProgressBarVisibility(false);
  }

  postProcessingOperationSucceeded(foundOccurances: FoundOccurancesPerHighlighter[]): void {
    console.log('PP succeeded');

    this.setPostProcessingProgressBarVisibility(false);

    if (!this.state.logContext) {
      return;
    }

    let searchTextFieldIndex: SearchTextFieldIndex | undefined;
    let filterTextFieldIndex: SearchTextFieldIndex | undefined;

    for (const found of foundOccurances) {
      let index: SearchTextFieldIndex | undefined;

      switch (found.highlighterId) {
        case TextFilterHighlighter.id:
          index = this.state.logContext.filterTextFieldIndex;
          break;
        case TextSearchHighlighter.id:
          index = this.state.logContext.searchTextFieldIndex;
          break;
        default:
          console.error('Unknown highlighter ID', found.highlighterId);
          continue;
      }

      let hasSameAmount: boolean;
      if (index) {
        hasSameAmount = index.totalAmount === found.array.length;
      } else {
        hasSameAmount = false;
      }

      if (!hasSameAmount) {
        index = {
          currentIndex: undefined,
          totalAmount: found.array.length,
        };
      }

      switch (found.highlighterId) {
        case TextFilterHighlighter.id:
          filterTextFieldIndex = index;
          break;
        case TextSearchHighlighter.id:
          searchTextFieldIndex = index;
          break;
      }
    }

    this.updateLogContext({
      searchTextFieldIndex,
      filterTextFieldIndex,
    });

    // Resolve pending search promise
    if (this.pendingSearchResolve) {
      this.pendingSearchResolve();
      this.pendingSearchResolve = undefined;
    }
  }
}
